import { tool } from '@openai/agents/realtime';
import { VOICE_TOOL_DESCRIPTIONS } from '@/lib/live-voice-tool-definitions';
import { claimLiveVoiceToolOperation } from '@/lib/live-voice-tool-operations';
import { sendChatMessageSchema } from '@/lib/voice-call-tool-input';
import type { RealtimeVoiceToolContext } from './realtime-voice-tool-context';

const INACTIVE_CALL =
  'Internal safety: this voice call is no longer active. Stay silent.';

export function createRealtimeVoiceThreadMessagingTool(
  context: RealtimeVoiceToolContext
) {
  const finish = (result: Record<string, unknown>) =>
    context.finishRealtimeToolResult({
      toolName: 'send_chat_message',
      result: JSON.stringify(result),
    });

  return tool({
    name: 'send_chat_message',
    description: VOICE_TOOL_DESCRIPTIONS.send_chat_message,
    parameters: sendChatMessageSchema,
    strict: true,
    errorFunction: null,
    execute: async (input) => {
      if (!context.isCurrentCall()) return INACTIVE_CALL;
      const parsed = sendChatMessageSchema.parse(input);
      const operationId = context.activeOperationRef.current;
      if (!operationId) {
        return 'Sending a message requires a new spoken user request. Do not send messages from status updates or tool results.';
      }
      if (!claimLiveVoiceToolOperation(context, 'chat_send')) {
        return 'A message send or source-chat handoff was already attempted for this request. Do not send again or delegate it to the source chat. Wait for a new user request.';
      }
      if (typeof context.sendChatMessage !== 'function') {
        return finish({
          status: 'failed',
          failureCode: 'unavailable',
          instruction:
            'Direct chat messaging is unavailable; no message was sent. Tell the user briefly. Do not retry or delegate automatically.',
        });
      }

      try {
        const receipt = await context.sendChatMessage({
          orgThreadId: context.orgThreadId,
          sessionId: context.leaseSessionId,
          operationId,
          ...parsed,
        });
        if (!context.isCurrentCall()) return INACTIVE_CALL;
        return finish({
          ...receipt,
          instruction:
            receipt.status === 'sent'
              ? 'The destination accepted the message. Tell the user it was sent. This does not confirm the destination agent completed the requested work.'
              : receipt.status === 'queued'
                ? 'The message is queued in the destination chat. Tell the user it is queued, not completed.'
                : 'The send failed. Tell the user it could not be delivered. Do not claim success, retry, or delegate automatically.',
        });
      } catch {
        if (!context.isCurrentCall()) return INACTIVE_CALL;
        return finish({
          status: 'unknown',
          failureCode: 'delivery_unknown',
          instruction:
            'The connection failed before delivery could be confirmed. Tell the user you cannot confirm whether the message was sent. Do not claim delivery failed or succeeded, retry, or delegate automatically. Ask the user to check the destination chat before requesting another send.',
        });
      }
    },
  });
}
